import * as React from "react";
import Loadable from "react-loadable";
import { RouteComponentProps, Route } from "react-router-dom";

import { ROUTES } from "@config/app.routing";
import { LOADABLE_COMPONENT_DELAY } from "@config/consts";

import { SERVICE_TYPE } from "@typings/globals";

import { ImpersonatorNotification } from "@ui/Molecules";
import { BookingsToValidateDashboardSkeleton } from "@ui/Skeletons/Skeletons";

import { getQueryValue } from "@services/QueryParamsParser";

import { useAuth } from "@hooks/useAuth";
import { useLocalisation } from "@hooks/useLocalisation";

import {
  BookingPriceUpdater,
  BookingStateFromFeatureToggleUpdater,
} from "@app/containers";
import { ServiceTypeAvailabilityChecker } from "@app/containers/InactiveServiceTypeChecker";
import {
  leaveProcess,
  setServiceLocation,
} from "@app/redux/booking/booking.actions";
import {
  fetchServiceLocationCoverage,
  resetState as resetServiceLocationState,
} from "@app/redux/serviceLocation/serviceLocation.actions";
import { useAppDispatch } from "@app/redux/hooks";

import { BookingView } from "./";

const LoadableThankYouPage: any = Loadable({
  loader: () => import("./BookingProcess/ThankYou"),
  render(loaded: any): JSX.Element {
    const Component = loaded.ThankYou;
    return <Component />;
  },
  loading: BookingsToValidateDashboardSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const Booking = (props: RouteComponentProps): React.ReactElement | null => {
  const dispatch = useAppDispatch();
  const { isUserAdmin, isAdminImpersonator } = useAuth();
  const { language } = useLocalisation();

  const zip = getQueryValue(props.location.search, "postalCode");
  const isFirstPage = props.history.location.pathname === ROUTES.BOOKING;

  const handleInvalidPostalCodeQuery = (): void => {
    window.location.href = `/${language}`;
  };

  React.useEffect(() => {
    (async () => {
      if (zip) {
        const { onSuccess } = await dispatch(
          fetchServiceLocationCoverage(zip, undefined, { saveData: true }),
        );

        onSuccess(payload => {
          dispatch(setServiceLocation(payload.data.serviceLocationId!));
        });
      }
    })();
  }, [zip, dispatch]);

  React.useEffect(() => {
    return () => {
      dispatch(leaveProcess());
      dispatch(resetServiceLocationState());
    };
  }, []);

  // redirect before any rendering logic begins
  if (!zip && isFirstPage) {
    handleInvalidPostalCodeQuery();
    return null;
  }

  if (isUserAdmin && !isAdminImpersonator) {
    return <ImpersonatorNotification />;
  }

  return (
    <React.Fragment>
      <Route exact path={ROUTES.BOOKING_THANK_YOU}>
        <LoadableThankYouPage />
      </Route>

      <Route exact path={[ROUTES.BOOKING, ROUTES.BOOKING_CHECKOUT]}>
        <BookingPriceUpdater />
        <ServiceTypeAvailabilityChecker
          serviceType={SERVICE_TYPE.HOME_CLEANING}
          postalCodeQuery={String(zip)}
        />
        <BookingStateFromFeatureToggleUpdater />
        <BookingView currentLocation={props.location.pathname} />
      </Route>
    </React.Fragment>
  );
};

export { Booking };
